/**
 * VisionMembership gRPC adapter for the invite → accept flow.
 *
 * Presents the accept page's wire shape (camelCase keys, lowercase role strings) on top of
 * the `protos.vision.membership.v1.VisionMembership` gRPC-web service, so the page is unaware
 * of the transport.
 */

import { createClient } from '@connectrpc/connect';
import { createGrpcWebTransport } from '@connectrpc/connect-web';
import { VisionMembership } from '../gen/vision/membership/v1/membership_service_pb';
import {
  inviteFromProto,
  acceptResultFromProto,
  inviteViewFromProto,
  memberViewFromProto,
  roleToProto,
} from './visionMembershipMapping';
import { config } from '../config';
import { getIdTokenForRequest } from './firebaseClient.js';

const BASE_URL = config.VISION_GRPC_BASE_URL;

const authInterceptor = (next) => async (req) => {
  const token = await getIdTokenForRequest();
  if (token) {
    req.header.set('authorization', `Bearer ${token}`);
  }
  return next(req);
};

let cachedClient = null;

function client() {
  if (!cachedClient) {
    const transport = createGrpcWebTransport({
      baseUrl: BASE_URL,
      interceptors: [authInterceptor],
    });
    cachedClient = createClient(VisionMembership, transport);
  }
  return cachedClient;
}

export async function fetchInviteByToken(token) {
  const response = await client().getVisionInviteByToken({ params: { token } });
  const invite = response.result?.invite;
  if (!invite) {
    throw new Error('Invite not found or has expired');
  }
  return inviteFromProto(invite);
}

export async function acceptInvite(token) {
  const response = await client().acceptVisionInvite({ params: { token } });
  return acceptResultFromProto(response.result);
}

export async function createVisionInvite({ projectId, email, role }) {
  await client().createVisionInvite({
    params: { projectId, email, role: roleToProto(role) },
  });
}

export async function listVisionInvites(projectId) {
  const response = await client().listVisionInvites({ params: { projectId } });
  return (response.result?.invites ?? []).map(inviteViewFromProto);
}

export async function revokeVisionInvite({ projectId, inviteId }) {
  await client().revokeVisionInvite({ params: { projectId, inviteId } });
}

export async function resendVisionInvite({ projectId, inviteId }) {
  await client().resendVisionInvite({ params: { projectId, inviteId } });
}

export async function listVisionMembers(projectId) {
  const response = await client().listVisionMembers({ params: { projectId } });
  return (response.result?.members ?? []).map(memberViewFromProto);
}

export async function changeVisionMemberRole({ projectId, accountId, role }) {
  await client().changeVisionMemberRole({
    params: { projectId, accountId, role: roleToProto(role) },
  });
}

export async function removeVisionMember({ projectId, accountId }) {
  await client().removeVisionMember({ params: { projectId, accountId } });
}

export async function leaveVisionProject({ projectId }) {
  await client().leaveVisionProject({ params: { projectId } });
}

export async function transferVisionProjectOwnership({ projectId, newOwnerAccountId }) {
  await client().transferVisionProjectOwnership({
    params: { projectId, newOwnerAccountId },
  });
}
